import React, { useState } from 'react';
import { FileText, Download } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge } from '../shared';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFtoText = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [extractedText, setExtractedText] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const loadingTask = getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;
                setPageCount(pdf.numPages);
                toast.success(`PDF loaded: ${pdf.numPages} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const extractText = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;

            let allText = '';

            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const page = await pdf.getPage(pageNum);
                const textContent = await page.getTextContent();

                const pageText = textContent.items.map(item => item.str).join(' ');
                allText += `\n\n=== Page ${pageNum} ===\n\n${pageText}`;

                setProgress(10 + ((pageNum / pdf.numPages) * 80));
            }

            setExtractedText(allText);
            setProgress(100);

            toast.success('Text extracted successfully!');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to extract text: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const downloadText = () => {
        const blob = new Blob([extractedText], { type: 'text/plain' });
        const fileName = file.name.replace(/\.pdf$/i, '');
        saveAs(blob, `${fileName}.txt`);
        toast.success('Text file downloaded!');
    };

    return (
        <div>
            {/* Header */}
            <div className="tool-header">
                <div className="tool-icon-wrapper">
                    <FileText size={32} />
                </div>
                <div className="tool-header-content">
                    <h1 className="tool-title">PDF to Text</h1>
                    <p className="tool-description">
                        Extract all text content from PDF documents and save as TXT file.
                    </p>
                </div>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPageCount(0); setExtractedText(''); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {!extractedText && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <Button variant="primary" size="lg" onClick={extractText} disabled={isProcessing} loading={isProcessing} fullWidth>
                                <FileText size={20} />
                                Extract Text
                            </Button>
                        </div>
                    )}

                    {extractedText && (
                        <>
                            <div style={{ marginTop: 'var(--spacing-6)' }}>
                                <Card>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
                                        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)' }}>
                                            Extracted Text
                                        </h3>
                                        <Button variant="secondary" size="sm" onClick={downloadText}>
                                            <Download size={16} />
                                            Download TXT
                                        </Button>
                                    </div>
                                    <div style={{
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                        padding: 'var(--spacing-4)',
                                        backgroundColor: 'var(--color-surface-elevated)',
                                        borderRadius: 'var(--radius-md)',
                                        fontSize: 'var(--text-sm)',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.6
                                    }}>
                                        {extractedText}
                                    </div>
                                </Card>
                            </div>

                            <div style={{ marginTop: 'var(--spacing-6)' }}>
                                <Button variant="primary" size="lg" onClick={downloadText} fullWidth>
                                    <Download size={20} />
                                    Download as TXT File
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default PDFtoText;
